import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';
import VendorProfileModal from '../components/VendorProfileModal';
import BackgroundIcons from '../components/BackgroundIcons';
import NotificationModal from '../components/NotificationModal';

const VendorDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [shortlists, setShortlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactVendor, setContactVendor] = useState(null);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [selectedVendorForPriority, setSelectedVendorForPriority] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(3);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, vendor: null });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    fetchVendorData();
  }, [navigate]);

  const fetchVendorData = async () => {
    try {
      setLoading(true);
      
      // Fetch user profile
      const userResponse = await apiService.users.getProfile();
      if (userResponse.data.success) {
        setUser(userResponse.data.user);
      }

      // Fetch shortlisted vendors
      const shortlistResponse = await fetch(`${API_ENDPOINTS.vendors}/shortlist`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (shortlistResponse.ok) {
        const shortlistData = await shortlistResponse.json();
        setShortlists(shortlistData.shortlists || []);
      }

    } catch (error) {
      console.error('Error fetching vendor data:', error);
    } finally {
      setLoading(false);
    }
  };

  // All the existing vendor management functions from the original dashboard
  const getDashboardStats = () => {
    const categories = shortlists.reduce((acc, vendor) => {
      let category;
      try {
        category = vendor.category;
        if (!category && vendor.vendor_data) {
          const vendorData = typeof vendor.vendor_data === 'string' ? JSON.parse(vendor.vendor_data) : vendor.vendor_data;
          category = vendorData.category;
        }
        category = category || 'Other';
      } catch (e) {
        category = vendor.category || 'Other';
      }
      
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    return {
      totalVendors: shortlists.length,
      categories: Object.keys(categories).length,
      highPriority: shortlists.filter(item => item.priority === 5).length,
      recentlyAdded: shortlists.filter(item => {
        const addedDate = new Date(item.added_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return addedDate > weekAgo;
      }).length
    };
  };

  const getFilteredShortlists = () => {
    return shortlists.filter(vendor => {
      let vendorCategory;
      try {
        vendorCategory = vendor.category;
        if (!vendorCategory && vendor.vendor_data) {
          const vendorData = typeof vendor.vendor_data === 'string' ? JSON.parse(vendor.vendor_data) : vendor.vendor_data;
          vendorCategory = vendorData.category;
        }
        vendorCategory = vendorCategory || 'Other';
      } catch (e) {
        vendorCategory = vendor.category || 'Other';
      }
      
      const categoryMatch = selectedCategories.length === 0 || selectedCategories.includes(vendorCategory);
      const priorityMatch = selectedPriorities.length === 0 || selectedPriorities.includes(vendor.priority);
      return categoryMatch && priorityMatch;
    });
  };

  const getAvailableCategories = () => {
    const categories = new Set();
    shortlists.forEach(vendor => {
      let category;
      try {
        category = vendor.category;
        if (!category && vendor.vendor_data) {
          const vendorData = typeof vendor.vendor_data === 'string' ? JSON.parse(vendor.vendor_data) : vendor.vendor_data;
          category = vendorData.category;
        }
        category = category || 'Other';
      } catch (e) {
        category = vendor.category || 'Other';
      }
      categories.add(category);
    });
    return Array.from(categories).sort();
  };

  const getAvailablePriorities = () => {
    const priorities = new Set(shortlists.map(vendor => vendor.priority || 3));
    return Array.from(priorities).sort((a, b) => b - a);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'venues': 'from-rose-100 to-pink-100',
      'photography': 'from-purple-100 to-indigo-100',
      'catering': 'from-green-100 to-emerald-100',
      'flowers': 'from-pink-100 to-rose-100',
      'music': 'from-blue-100 to-cyan-100',
      'attire': 'from-gold-100 to-yellow-100',
      'transportation': 'from-gray-100 to-slate-100'
    };
    return colors[category?.toLowerCase()] || 'from-gray-100 to-slate-100';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'venues': '🏛️',
      'catering': '🍽️',
      'photography': '📸',
      'flowers': '🌸',
      'music': '🎵',
      'attire': '👗',
      'transportation': '🚗'
    };
    return icons[category?.toLowerCase()] || '🏪';
  };

  const changePriority = (vendor) => {
    setSelectedVendorForPriority(vendor);
    setSelectedPriority(vendor.priority || 3);
    setShowPriorityModal(true);
  };

  const updatePriority = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.vendors}/shortlist/update-priority`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          vendor_name: selectedVendorForPriority.vendor_name,
          vendor_category: selectedVendorForPriority.vendor_category,
          priority: selectedPriority
        })
      });

      if (response.ok) {
        setShortlists(prev => prev.map(v => 
          v.id === selectedVendorForPriority.id 
            ? { ...v, priority: selectedPriority }
            : v
        ));
        setShowPriorityModal(false);
      }
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  const deleteVendor = async (vendor) => {
    console.log('🗑️ Removing vendor from shortlist:', vendor.id);
    try {
      const response = await fetch(`${API_ENDPOINTS.vendors}/shortlist/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          shortlist_id: vendor.id
        })
      });

      if (response.ok) {
        setShortlists(prev => prev.filter(v => v.id !== vendor.id));
      } else {
        const errorData = await response.json();
        console.error('Error removing vendor:', errorData);
      }
    } catch (error) {
      console.error('Error deleting vendor:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    await deleteVendor(deleteConfirm.vendor);
    setDeleteConfirm({ show: false, vendor: null });
  };

  const viewVendorProfile = (vendor) => {
    let vendorData;
    try {
      vendorData = vendor.vendor_data ? 
        (typeof vendor.vendor_data === 'string' ? JSON.parse(vendor.vendor_data) : vendor.vendor_data) 
        : {};
    } catch (e) {
      vendorData = {};
    }

    // Extract services from nested highlights array (same logic as SmartVendorMatching)
    let services = [];
    if (vendorData.highlights && vendorData.highlights.length > 0) {
      // highlights is an array containing another array
      services = Array.isArray(vendorData.highlights[0]) ? vendorData.highlights[0] : vendorData.highlights;
    }

    const profileData = {
      name: vendor.vendor_name || 'Unknown Vendor',
      category: vendor.category || vendorData.category || 'Wedding Services',
      tagline: vendorData.tagline || 'Professional wedding services',
      description: vendorData.description || 'No description available',
      rating: vendorData.rating || 4.5,
      reviews: vendorData.reviews || 0,
      location: vendorData.location || 'Location not specified',
      price: vendorData.price_range || vendorData.price || 'Contact for pricing',
      price_range: vendorData.price_range || vendorData.price || 'Contact for pricing',
      style: vendorData.style || 'Various styles available',
      services: services,
      highlights: services
    };

    setSelectedVendor(profileData);
    setShowProfile(true);
  };

  const closeProfile = () => {
    setShowProfile(false);
    setSelectedVendor(null);
  };

  // Group vendors by category
  const categorizedShortlists = getFilteredShortlists().reduce((acc, vendor) => {
    let category;
    try {
      category = vendor.category;
      if (!category && vendor.vendor_data) {
        const vendorData = typeof vendor.vendor_data === 'string' ? JSON.parse(vendor.vendor_data) : vendor.vendor_data;
        category = vendorData.category;
      }
      category = category || 'Other';
    } catch (e) {
      category = vendor.category || 'Other';
    }
    
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(vendor);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your vendors...</p>
        </div>
      </div>
    );
  }

  const stats = getDashboardStats();

  const vendorIcons = [
    { emoji: '🏪', size: '4xl', opacity: '15' },
    { emoji: '📸', size: '4xl', opacity: '15' },
    { emoji: '🌸', size: '4xl', opacity: '15' },
    { emoji: '🎵', size: '4xl', opacity: '15' },
    { emoji: '👗', size: '4xl', opacity: '15' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={vendorIcons} />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        
        {/* Beautiful Header - VowAI Style */}
        <div className="text-center mb-16 relative">
          
          {/* Back to Dashboard Button */}
          <div className="absolute top-0 left-0">
            <a 
              href="/dashboard"
              className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-6 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
            >
              <span className="mr-2 text-xl group-hover:animate-bounce">←</span>
              Back to Dashboard
              {/* VowAI signature glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            </a>
          </div>
          
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-purple-400 via-blue-400 to-emerald-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-4xl animate-pulse">🏪</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide animate-fadeInUp">
            Vendor Management
          </h1>
          
          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">🏪</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent rounded-full"></div>
          </div>
          
          <p className="text-2xl text-gray-700 font-light italic animate-fadeInUp delay-300">
            Manage your shortlisted wedding vendors ✨
          </p>
          
          <div className="mt-8">
            <span className="px-6 py-3 bg-gradient-to-r from-purple-400 to-emerald-500 text-white rounded-full font-bold text-lg shadow-xl animate-pulse">
              {stats.totalVendors} Vendors Shortlisted
            </span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="group bg-white rounded-2xl p-6 shadow-lg border border-rose-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Vendors</p>
                <p className="text-3xl font-bold text-rose-600">{stats.totalVendors}</p>
              </div>
              <div className="bg-rose-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">🏪</span>
              </div>
            </div>
          </div>

          <div className="group bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Categories</p>
                <p className="text-3xl font-bold text-purple-600">{stats.categories}</p>
              </div>
              <div className="bg-purple-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">📂</span>
              </div>
            </div>
          </div>

          <div className="group bg-white rounded-2xl p-6 shadow-lg border border-gold-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">High Priority</p>
                <p className="text-3xl font-bold text-gold-600">{stats.highPriority}</p>
              </div>
              <div className="bg-gold-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">⭐</span>
              </div>
            </div>
          </div>

          <div className="group bg-white rounded-2xl p-6 shadow-lg border border-green-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Recently Added</p>
                <p className="text-3xl font-bold text-green-600">{stats.recentlyAdded}</p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">🆕</span>
              </div>
            </div>
          </div>
        </div>

        {/* Vendor Management Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-rose-100 p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="text-3xl mr-3 animate-bounce">📋</span>
            Your Shortlisted Vendors
          </h2>

          {/* Filters */}
          <div className="mb-6 bg-gradient-to-br from-white/80 to-rose-50/80 backdrop-blur-lg rounded-3xl p-6 border-2 border-gold-200/50 shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Category Filter */}
              <div>
                <label className="block text-lg font-bold text-gray-800 mb-3 flex items-center">
                  <span className="text-2xl mr-2">🏷️</span>
                  Filter by Category
                </label>
                <div className="flex flex-wrap gap-2">
                  {getAvailableCategories().map(category => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategories(prev => 
                          prev.includes(category) 
                            ? prev.filter(c => c !== category)
                            : [...prev, category]
                        );
                      }}
                      className={`group/catbtn px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 transform hover:scale-105 shadow-lg ${
                        selectedCategories.includes(category)
                          ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-rose-300'
                          : 'bg-gradient-to-r from-white to-rose-50 text-gray-700 hover:from-rose-100 hover:to-pink-100 border border-rose-200'
                      }`}
                    >
                      <span className="mr-1 group-hover/catbtn:animate-bounce">{getCategoryIcon(category)}</span>
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-lg font-bold text-gray-800 mb-3 flex items-center">
                  <span className="text-2xl mr-2">⭐</span>
                  Filter by Priority
                </label>
                <div className="flex flex-wrap gap-2">
                  {getAvailablePriorities().map(priority => (
                    <button
                      key={priority}
                      onClick={() => {
                        setSelectedPriorities(prev => 
                          prev.includes(priority) 
                            ? prev.filter(p => p !== priority)
                            : [...prev, priority]
                        );
                      }}
                      className={`group/pribtn px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 transform hover:scale-105 shadow-lg ${
                        selectedPriorities.includes(priority)
                          ? 'bg-gradient-to-r from-gold-500 to-yellow-500 text-white shadow-gold-300'
                          : 'bg-gradient-to-r from-white to-gold-50 text-gray-700 hover:from-gold-100 hover:to-yellow-100 border border-gold-200'
                      }`}
                    >
                      {priority === 5 ? <><span className="group-hover/pribtn:animate-bounce inline-block">🔥</span> High</> : 
                       priority === 1 ? <><span className="group-hover/pribtn:animate-bounce inline-block">🌱</span> Low</> : 
                       priority === 3 ? <><span className="group-hover/pribtn:animate-bounce inline-block">⚡</span> Medium</> :
                       <><span className="group-hover/pribtn:animate-bounce inline-block">⭐</span> Priority {priority}</>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Vendor Display */}
          {Object.keys(categorizedShortlists).length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No vendors shortlisted yet</h3>
              <p className="text-gray-500 mb-6">Start exploring vendors to build your dream team!</p>
              <a 
                href="/vendor-vault/smart-vendor-matching"
                className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-6 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
              >
                <span className="relative z-10 flex items-center justify-center">
                  <span className="mr-2 text-xl animate-bounce">🚀</span>
                  Find Vendors
                </span>
                {/* VowAI signature glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              </a>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(categorizedShortlists).map(([category, vendors]) => (
                <div key={category} className="relative">
                  {/* Category Header */}
                  <div className={`bg-gradient-to-r ${getCategoryColor(category)} rounded-3xl p-6 mb-6 border-2 border-white/50 shadow-xl`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-white/30 rounded-2xl flex items-center justify-center shadow-lg">
                          <span className="text-4xl">{getCategoryIcon(category)}</span>
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-800 capitalize">
                            {category}
                          </h3>
                          <p className="text-gray-700 font-medium">
                            {vendors.length} vendor{vendors.length !== 1 ? 's' : ''} shortlisted
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="px-4 py-2 bg-white/60 rounded-full border border-white/80 shadow-lg">
                          <span className="font-bold text-gray-800">{vendors.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Vendor Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {vendors.map((vendor, index) => (
                      <div 
                        key={index} 
                        className="group relative bg-white/80 backdrop-blur-lg rounded-3xl p-6 border-2 border-gold-200/50 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-500 overflow-hidden"
                      >
                        {/* Decorative Background Elements */}
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-rose-200/30 to-pink-300/30 rounded-full -translate-y-10 translate-x-10"></div>
                        <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-gold-200/30 to-yellow-300/30 rounded-full translate-y-8 -translate-x-8"></div>
                        
                        {/* Vendor Content */}
                        <div className="relative z-10">
                          {/* Vendor Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <button 
                                onClick={() => viewVendorProfile(vendor)}
                                className="text-left group-hover:text-rose-600 transition-colors duration-300"
                              >
                                <h4 className="text-xl font-bold text-gray-800 mb-1 group-hover:text-rose-600">
                                  {vendor.vendor_name}
                                </h4>
                              </button>
                              
                              {/* Category Badge */}
                              <div className={`inline-flex items-center px-3 py-1 bg-gradient-to-r ${getCategoryColor(category)} rounded-full border border-white/50 shadow-md`}>
                                <span className="text-sm mr-1 group-hover:animate-bounce">{getCategoryIcon(category)}</span>
                                <span className="text-xs font-bold text-gray-700 capitalize">{category}</span>
                              </div>
                            </div>
                            
                            {/* Priority Display */}
                            <div className="flex flex-col items-end">
                              <button
                                onClick={() => setDeleteConfirm({ show: true, vendor: vendor })}
                                className="group/delete text-gray-400 hover:text-red-500 transition-colors p-2 rounded-full hover:bg-red-50 mb-2"
                                title="Remove vendor"
                              >
                                <span className="text-lg group-hover/delete:animate-bounce">🗑️</span>
                              </button>
                              <button
                                onClick={() => changePriority(vendor)}
                                className="group/prioritybtn flex items-center mb-1 hover:bg-gray-100 px-2 py-1 rounded-lg transition-colors cursor-pointer"
                                title="Change priority"
                              >
                                <span className="text-lg mr-1 group-hover/prioritybtn:animate-bounce">
                                  {vendor.priority === 5 ? '🔥' : 
                                   vendor.priority === 1 ? '🌱' : 
                                   '⚡'}
                                </span>
                                <span className="text-sm font-bold text-gray-700">
                                  {vendor.priority === 5 ? 'High' : 
                                   vendor.priority === 1 ? 'Low' : 
                                   'Medium'}
                                </span>
                              </button>
                              <span className="text-xs text-gray-600 font-medium">Priority</span>
                            </div>
                          </div>
                          
                          {/* Vendor Details */}
                          {vendor.notes && (
                            <div className="bg-gradient-to-r from-white/60 to-rose-50/60 rounded-2xl p-4 mb-4 border border-rose-200/50">
                              <p className="text-sm text-gray-700 italic">"{vendor.notes}"</p>
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2 mt-4">
                            <button 
                              onClick={() => viewVendorProfile(vendor)}
                              className="group/viewbtn flex-1 relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-2 px-4 rounded-xl font-bold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300 animate-pulse"
                            >
                              <span className="relative z-10 flex items-center justify-center">
                                <span className="mr-1 group-hover/viewbtn:animate-bounce">✨</span>
                                View Profile
                              </span>
                              {/* VowAI signature glow effect */}
                              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                            </button>
                          </div>
                        </div>
                        
                        {/* Hover Effect Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-br from-rose-500/5 to-gold-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Vendor Profile Modal */}
      {showProfile && selectedVendor && (
        <VendorProfileModal 
          isOpen={showProfile}
          vendor={selectedVendor}
          onClose={closeProfile}
          source="dashboard"
        />
      )}

      {/* Priority Change Modal - Reusing VendorProfileModal design */}
      {showPriorityModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-rose-200/50 max-w-lg w-full">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 p-6 rounded-t-3xl relative">
              <button
                onClick={() => setShowPriorityModal(false)}
                className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl font-bold hover:rotate-90 transition-all duration-300"
              >
                ×
              </button>
              
              <div className="text-center">
                <div className="text-3xl mb-2 animate-bounce">⭐</div>
                <h2 className="text-xl font-bold text-white mb-1">Set Priority for {selectedVendorForPriority?.vendor_name}</h2>
                <p className="text-white/90 text-sm">Choose how important this vendor is to you</p>
              </div>
            </div>

            {/* Priority Options */}
            <div className="p-6 space-y-4">
              
              {/* High Priority */}
              <div 
                onClick={() => setSelectedPriority(5)}
                className={`group/priority cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                  selectedPriority === 5 
                    ? 'border-red-300 bg-gradient-to-r from-red-50 to-pink-50 shadow-lg' 
                    : 'border-gray-200 bg-gray-50 hover:border-red-200 hover:bg-red-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl group-hover/priority:animate-bounce">🔥</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">High Priority</div>
                    <div className="text-sm text-gray-600">Must-have vendor for your wedding</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedPriority === 5 ? 'bg-red-500 border-red-500' : 'border-gray-300'
                  }`}></div>
                </div>
              </div>

              {/* Medium Priority */}
              <div 
                onClick={() => setSelectedPriority(3)}
                className={`group/priority cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                  selectedPriority === 3 
                    ? 'border-yellow-300 bg-gradient-to-r from-yellow-50 to-orange-50 shadow-lg' 
                    : 'border-gray-200 bg-gray-50 hover:border-yellow-200 hover:bg-yellow-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl group-hover/priority:animate-bounce">⚡</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">Medium Priority</div>
                    <div className="text-sm text-gray-600">Important but flexible option</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedPriority === 3 ? 'bg-yellow-500 border-yellow-500' : 'border-gray-300'
                  }`}></div>
                </div>
              </div>

              {/* Low Priority */}
              <div 
                onClick={() => setSelectedPriority(1)}
                className={`group/priority cursor-pointer p-4 rounded-xl border-2 transition-all duration-300 ${
                  selectedPriority === 1 
                    ? 'border-green-300 bg-gradient-to-r from-green-50 to-teal-50 shadow-lg' 
                    : 'border-gray-200 bg-gray-50 hover:border-green-200 hover:bg-green-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl group-hover/priority:animate-bounce">🌱</div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-800">Low Priority</div>
                    <div className="text-sm text-gray-600">Nice to have, budget permitting</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedPriority === 1 ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}></div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button 
                  onClick={() => setShowPriorityModal(false)}
                  className="flex-1 relative overflow-hidden bg-gradient-to-r from-gray-400 via-gray-500 to-gray-600 text-white py-3 px-4 rounded-xl font-bold hover:shadow-lg transition-all duration-300"
                >
                  Cancel
                </button>
                <button 
                  onClick={updatePriority}
                  className="flex-1 relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-4 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 animate-pulse"
                >
                  <span className="relative z-10">Update Priority</span>
                  {/* VowAI signature glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out;
        }
      `}</style>

      {/* Delete Confirmation Modal */}
      <NotificationModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, vendor: null })}
        type="warning"
        title="Remove Vendor?"
        message="Are you sure you want to remove this vendor from your shortlist? This action cannot be undone."
        isConfirmation={true}
        onConfirm={handleDeleteConfirm}
        confirmText="Yes, Remove"
        cancelText="Cancel"
      />
    </div>
  );
};

export default VendorDashboard;
